import React, { createContext, useContext } from 'react';

/**
 * PREVIEW MODE CONTEXT
 * 
 * Provides mock data for read-only interactive preview.
 * NO MongoDB, NO authentication, NO mutations.
 * This is a frontend-only interactive preview.
 */

const PreviewContext = createContext(null);

// Mock user for preview
const MOCK_USER = {
  id: 'preview-user',
  email: 'preview@example.com',
  name: 'Preview User',
  country: 'AU',
  role: 'user',
  onboarding_completed: true,
  has_seen_first_return: true,
  plan: 'preview'
};

// Mock blood results - Realistic CLL pattern showing treatment response to Ibrutinib
// Shows diagnosis, progression during watch & wait, then improvement after starting Ibrutinib
const MOCK_BLOOD_RESULTS = [
  {
    id: 'blood-1',
    date: '2022-03-14',
    wbc: 6.8,
    lymphocytes: 2.4,
    platelets: 235,
    haemoglobin: 148,
    neutrophils: 3.8,
    notes: 'Diagnosis confirmed'
  },
  {
    id: 'blood-2',
    date: '2022-11-08',
    wbc: 8.2,
    lymphocytes: 3.9,
    platelets: 218,
    haemoglobin: 142,
    neutrophils: 3.5,
    notes: 'Watch and wait'
  },
  {
    id: 'blood-3',
    date: '2023-06-22',
    wbc: 11.4,
    lymphocytes: 6.8,
    platelets: 195,
    haemoglobin: 138,
    neutrophils: 3.9,
    notes: 'Monitoring progression'
  },
  {
    id: 'blood-4',
    date: '2024-02-15',
    wbc: 18.2,
    lymphocytes: 14.1,
    platelets: 165,
    haemoglobin: 125,
    neutrophils: 3.2,
    notes: 'Treatment discussion - rising counts'
  },
  {
    id: 'blood-5',
    date: '2024-08-03',
    wbc: 24.5,
    lymphocytes: 19.8,
    platelets: 148,
    haemoglobin: 118,
    neutrophils: 2.9,
    notes: 'Pre-treatment baseline'
  },
  // IBRUTINIB STARTED 2024-08-15 - Results now show improvement
  {
    id: 'blood-6',
    date: '2024-11-20',
    wbc: 18.3,
    lymphocytes: 14.2,
    platelets: 162,
    haemoglobin: 124,
    neutrophils: 3.1,
    notes: '3 months on Ibrutinib - early response'
  },
  {
    id: 'blood-7',
    date: '2025-03-15',
    wbc: 12.1,
    lymphocytes: 8.4,
    platelets: 185,
    haemoglobin: 134,
    neutrophils: 3.4,
    notes: '7 months on Ibrutinib - strong response'
  },
  {
    id: 'blood-8',
    date: '2025-07-22',
    wbc: 8.6,
    lymphocytes: 4.9,
    platelets: 208,
    haemoglobin: 142,
    neutrophils: 3.6,
    notes: '11 months on Ibrutinib - near remission'
  },
  {
    id: 'blood-9',
    date: '2025-12-10',
    wbc: 6.2,
    lymphocytes: 2.8,
    platelets: 225,
    haemoglobin: 148,
    neutrophils: 3.9,
    notes: '16 months on Ibrutinib - complete remission'
  }
];

// Mock nutrition data (Vitamin D minimum)
const MOCK_NUTRITION_RECORDS = [
  {
    id: 'nutr-1',
    date: '2024-03-15',
    vitamin_d: 45,
    b12: 380,
    folate: 18,
    ferritin: 85,
    notes: 'Vitamin D slightly low'
  },
  {
    id: 'nutr-2',
    date: '2024-06-20',
    vitamin_d: 62,
    b12: 420,
    folate: 22,
    ferritin: 92,
    notes: 'Improvement after supplementation'
  },
  {
    id: 'nutr-3',
    date: '2024-09-10',
    vitamin_d: 78,
    b12: 450,
    folate: 25,
    ferritin: 98,
    notes: 'Great progress'
  },
  {
    id: 'nutr-4',
    date: '2024-12-05',
    vitamin_d: 85,
    b12: 480,
    folate: 28,
    ferritin: 105,
    notes: 'Optimal levels reached'
  }
];

// Mock treatments - Aligned with blood results timeframe (2022-2025)
const MOCK_TREATMENTS = [
  {
    id: 'treat-1',
    name: 'Vitamin D3',
    category: 'vitamin',
    dosage: '4000 IU',
    frequency: 'daily',
    start_date: '2022-04-01',
    end_date: null,
    notes: 'Started at diagnosis for deficiency'
  },
  {
    id: 'treat-5',
    name: 'B12',
    category: 'vitamin',
    dosage: '1000mcg',
    frequency: 'daily',
    start_date: '2022-03-15',
    end_date: null,
    notes: 'Methylcobalamin for energy support'
  },
  {
    id: 'treat-2',
    name: 'Turkey Tail',
    category: 'supplement',
    dosage: '2g',
    frequency: 'twice daily',
    start_date: '2022-06-15',
    end_date: null,
    notes: 'Immune support - ongoing'
  },
  {
    id: 'treat-3',
    name: 'Green Tea Extract',
    category: 'other',
    dosage: '500mg',
    frequency: 'daily',
    start_date: '2023-01-10',
    end_date: '2024-06-30',
    notes: 'EGCG supplementation - completed 18 month course'
  },
  {
    id: 'treat-4',
    name: 'Ibrutinib',
    category: 'medication',
    dosage: '420mg',
    frequency: 'daily',
    start_date: '2024-08-15',
    end_date: null,
    notes: 'BTK inhibitor - started treatment'
  }
];

// Mock notes/ideas (2-3 entries)
const MOCK_IDEAS = [
  {
    id: 'idea-1',
    title: 'Ask about Venetoclax combination',
    category: 'question',
    source_page: '/blood-markers',
    source_label: 'Blood Markers',
    note: 'Research shows promising results for treatment-naive patients. Discuss at next appointment.',
    saved_at: '2024-11-15T10:30:00Z'
  },
  {
    id: 'idea-2',
    title: 'Vitamin D optimal range',
    category: 'research',
    source_page: '/vitamin-d-education',
    source_label: 'Vitamin D',
    note: 'Aim for 75-100 nmol/L. Higher end of range may be beneficial for CLL patients.',
    saved_at: '2024-10-20T14:15:00Z'
  },
  {
    id: 'idea-3',
    title: 'Walking routine for fatigue',
    category: 'lifestyle',
    source_page: '/stress-wellbeing',
    source_label: 'Stress & Wellbeing',
    note: 'Start with 15-minute walks, gradually increase. Morning walks may help with energy levels.',
    saved_at: '2024-09-05T09:00:00Z'
  }
];

// Mock alerts
const MOCK_ALERTS = [
  {
    id: 'alert-1',
    type: 'info',
    message: 'Your lymphocyte count has been stable for 3 months',
    created_at: '2024-12-01T08:00:00Z',
    is_read: false
  }
];

// Mock clinical ranges
const MOCK_CLINICAL_RANGES = {
  wbc: { min: 4.0, max: 11.0, unit: 'x10⁹/L' },
  lymphocytes: { min: 1.0, max: 4.0, unit: 'x10⁹/L' },
  platelets: { min: 150, max: 400, unit: 'x10⁹/L' },
  haemoglobin: { min: 130, max: 180, unit: 'g/L' },
  neutrophils: { min: 2.0, max: 7.5, unit: 'x10⁹/L' }
};

export const PreviewProvider = ({ children }) => {
  const value = {
    isPreviewMode: true,
    user: MOCK_USER,
    bloodResults: MOCK_BLOOD_RESULTS,
    nutritionRecords: MOCK_NUTRITION_RECORDS,
    treatments: MOCK_TREATMENTS,
    ideas: MOCK_IDEAS,
    alerts: MOCK_ALERTS,
    clinicalRanges: MOCK_CLINICAL_RANGES
  };

  return (
    <PreviewContext.Provider value={value}>
      {children}
    </PreviewContext.Provider>
  );
};

export const usePreview = () => {
  const context = useContext(PreviewContext);
  if (!context) {
    throw new Error('usePreview must be used within PreviewProvider');
  }
  return context;
};

export default PreviewContext;
